import { ColumnTypes, FilterTypes } from 'commons/table/Constants';
import { Badge } from 'react-bootstrap';
import { WrapTextAndCenterAlign } from 'components/WrapText';
import { formatDateWithTime } from 'services/MomentUtils';
import {
  QUERY_FILTER_TYPES,
  normalizeToStringArray,
} from "commons/utils/queryParamUtils";
const SWITCHES_MAP = [
  { key: 'false', value: 'Non-Associated' },
  { key: 'true', value: 'Associated' },
]

export const UNKNOWN = "Unknown";
export const ALL_FAX_TAG = { id: "ALL", name: "ALL" };

const parseTagsFromUrl = (rawValue, urlFilterValues) => {
  const customerTags = Array.isArray(rawValue) ? rawValue : [];
  return Array.from(
    new Set([urlFilterValues.faxProgramCode, ...customerTags].filter(Boolean))
  );
};

const serializeTagsToUrl = (filter, urlFilterValues) => {
  const selectedTags = normalizeToStringArray(filter?.value);
  return selectedTags
    .filter((tag) => tag !== (urlFilterValues.faxProgramCode || ""))
    .join(",");
};

export const UNPROCESSED_FAX_PRIORITY = {
  ALERT: "Alert",
  CONTACT: "Contact",
  REVIEW: "Review",
  PROCESSING: "Processing",
  UNLINKED: "Unlinked",
  OTHER: "Other",
}

export const UNPROCESSED_FAX_PRIORITY_SINGLE_SELECT_OPTIONS = [
  { key: "", value: "" },
  { key: "ALERT", value: "Alert" },
  { key: "CONTACT", value: "Contact" },
  { key: "PROCESSING", value: "Processing" },
  { key: "REVIEW", value: "Review" },
  { key: "UNLINKED", value: "Unlinked" },
  { key: "OTHER", value: "Other" },
]

export const UNPROCESSED_FAX_PROGRAM_TYPE = {
  SWITCH: "SWITCH",
  NON_SWITCH: "NON_SWITCH",
  UNKNOWN: "UNKNOWN"
}

export const UNPROCESSED_FAX_STATUSES = {
  UNPROCESSED: "UNPROCESSED",
  LINKING_IN_PROGRESS: "LINKING IN-PROGRESS",
  PARTIALLY_LINKED: "PARTIALLY LINKED",
  FAILED: "FAILED",
}

export const FAX_DETAILS_UI_RULES_DEFAULT_KEY = "DEFAULT";

export const UNPROCESSED_FAX_PROGRAM_CODE = {
  DMR: "DMR",
};

export const DMR_FAX_REFERENCE_ID_MASK = "00000000-000-0000";

export const LINK_FIELD_VALIDATION = {
  STRING: "string",
  NUMERIC_STRING: "numericString",
  MASKED_STRING: "maskedString",
  PAGE_NUMBER: "pageNumber",
  REQUIRED_STRING: "requiredString",
  OPTIONAL_STRING: "optionalString",
};

/** Level 1: fax program type (SWITCH | NON_SWITCH | UNKNOWN). Level 2: fax program code with DEFAULT fallback. */
export const FAX_DETAILS_UI_RULES = {
  [UNPROCESSED_FAX_PROGRAM_TYPE.SWITCH]: {
    [FAX_DETAILS_UI_RULES_DEFAULT_KEY]: {
      key: UNPROCESSED_FAX_PROGRAM_TYPE.SWITCH,
      headingText: "Link Fax Page To Switch",
      showPriority: true,
      showTags: true,
      showLinkSection: true,
      showNotes: true,
      showMemberSwitchList: true,
      showDiscard: true,
      showAssociatedSwitch: true,
      navigateBackAfterTagSave: false,
      tagsButtonLabel: "Save Tags",
      showBypassAutoDisposition: true,
      bypassAutoDispositionLabel: "Bypass Auto Disposition",
      linkButtonLabel: "Link Switch(es)",
      linkFieldsConfig: [
        {
          name: "identifier",
          label: "Switch Id",
          apiField: "selectedGlobalSwitchId",
          inputType: "text",
          disabled: true,
          validation: { kind: LINK_FIELD_VALIDATION.STRING },
        },
        {
          name: "referenceField",
          label: "Change Order",
          apiField: "pageIndex",
          inputType: "number",
          validation: { kind: LINK_FIELD_VALIDATION.PAGE_NUMBER },
        },
        {
          name: "additionalField",
          label: "Additional Pages",
          apiField: "additionalPages",
          inputType: "text",
          placeholder: "e.g., 1,2,3",
          helperText: "Enter page numbers as shown on the PDF (comma-separated)",
          validation: { kind: LINK_FIELD_VALIDATION.OPTIONAL_STRING },
        },
      ],
      linkConfirmModal: {
        title: "Confirm Link Switches",
        body: "The entered pages will be linked to the selected switch(es) and removed from the pdf view. Continue?",
        bodyWhenBypass:
          "The entered pages will be linked to the selected switch(es) with Bypass Auto Disposition enabled and removed from the pdf view. Continue?",
      },
    },
  },
  [UNPROCESSED_FAX_PROGRAM_TYPE.NON_SWITCH]: {
    [FAX_DETAILS_UI_RULES_DEFAULT_KEY]: {
      key: UNPROCESSED_FAX_PROGRAM_TYPE.NON_SWITCH,
      headingText: "Link Fax to Non-Switch Entity",
      showPriority: true,
      showTags: true,
      showLinkSection: true,
      showNotes: true,
      showPrescriberList: true,
      showDiscard: true,
      showAssociatedSwitch: true,
      navigateBackAfterTagSave: false,
      tagsButtonLabel: "Save Tags",
      showBypassAutoDisposition: false,
      bypassAutoDispositionLabel: "Bypass Auto Disposition",
      linkButtonLabel: "Link and Archive",
      linkFieldsConfig: [
        {
          name: "identifier",
          label: "Prescriber NPI",
          apiField: "prescriberNpi",
          inputType: "text",
          disabled: true,
          validation: { kind: LINK_FIELD_VALIDATION.STRING },
        },
        {
          name: "referenceField",
          label: "Fax Reference ID",
          apiField: "faxReferenceId",
          inputType: "text",
          validation: { kind: LINK_FIELD_VALIDATION.NUMERIC_STRING },
        },
        {
          name: "additionalField",
          label: "Link Pages",
          apiField: "linkPages",
          inputType: "text",
          placeholder: "e.g., 1,2,3",
          helperText: "Enter page numbers as shown on the PDF (comma-separated)",
          validation: { kind: LINK_FIELD_VALIDATION.REQUIRED_STRING },
        },
      ],
      linkConfirmModal: {
        title: "Confirm Link and Archive",
        body: "The entered pages will be linked to the prescriber, archived and removed from the pdf view. Continue?",
      },
    },
    [UNPROCESSED_FAX_PROGRAM_CODE.DMR]: {
      key: UNPROCESSED_FAX_PROGRAM_TYPE.NON_SWITCH,
      headingText: "Link Fax to DMR Entity",
      showPriority: true,
      showTags: true,
      showLinkSection: true,
      showNotes: true,
      showPrescriberList: false,
      showCustomList: {
        id: "dmr-verify-list",
        title: "DMR Verification",
        description:
          "Enter a fax reference ID to verify and select from matching records below.",
        verifyInput: {
          label: "Fax Reference ID",
          paramKey: "faxReferenceId",
          required: true,
          requireCompleteMask: true,
          inputMask: {
            pattern: DMR_FAX_REFERENCE_ID_MASK,
            emptyChar: "*",
            submitFormat: "masked",
          },
          maskHelperText: "",
        },
        verifyButton: {
          label: "View Details",
          variant: "primary",
        },
        verifyApi: {
          method: "POST",
          url: "/conversions/reference-ids/resolve",
          bodyKey: "referenceIds",
          bodyValueAsArray: true,
        },
        selection: {
          enabled: true,
          rowIdKey: "faxReferenceId",
          linkPageField: "identifier",
          enableAllRowSelect: false,
          singleSelect: true,
          clearSelectionOnVerify: true,
          preserveLinkPagesOnVerify: true,
          populateFields: [
            {
              from: "faxReferenceId",
              to: "identifier",
              formatMask: DMR_FAX_REFERENCE_ID_MASK,
            },
          ],
        },
        table: {
          showFilters: false,
          enableSorting: false,
          rowIdKey: "faxReferenceId",
          defaultPageSize: 5,
          pageSizeOptions: [5, 10, 20],
          showPagination: false,
          emptyBeforeVerifyMessage:
            "Enter a fax reference ID and click Verify to load records.",
          emptyMessage: "No records found for this fax reference ID.",
          columns: [
            {
              header: "Fax Reference ID",
              accessor: "faxReferenceId",
              accessorKey: "faxReferenceId",
              width: 160,
              enableSorting: false,
              enableColumnFilter: false,
              columnType: "Text",
            },
            {
              header: "Member Name",
              accessor: "memberName",
              accessorKey: "memberName",
              width: 180,
              enableSorting: false,
              enableColumnFilter: false,
              columnType: "Text",
            },
            {
              header: "Prescriber Name",
              accessor: "prescriberName",
              accessorKey: "prescriberName",
              width: 200,
              enableSorting: false,
              enableColumnFilter: false,
              columnType: "Text",
            },
            {
              header: "Prescriber NPI",
              accessor: "prescriberNpi",
              accessorKey: "prescriberNpi",
              width: 130,
              enableSorting: false,
              enableColumnFilter: false,
              columnType: "Text",
            },
            {
              header: "Preferred Drug",
              accessor: "preferredDrugName",
              accessorKey: "preferredDrugName",
              width: 160,
              enableSorting: false,
              enableColumnFilter: false,
              columnType: "Text",
            },
          ],
        },
      },
      showDiscard: true,
      showAssociatedSwitch: true,
      navigateBackAfterTagSave: false,
      tagsButtonLabel: "Save Tags",
      showBypassAutoDisposition: true,
      bypassAutoDispositionLabel: "Bypass Auto Disposition",
      linkButtonLabel: "Verify and Proceed",
      linkFieldsConfig: [
        {
          name: "identifier",
          label: "Fax Reference ID",
          apiField: "faxReferenceId",
          inputType: "text",
          disabled: true,
          validation: {
            kind: LINK_FIELD_VALIDATION.MASKED_STRING,
            maskPattern: DMR_FAX_REFERENCE_ID_MASK,
          },
        },
        {
          name: "additionalField",
          label: "Link Pages",
          apiField: "linkPages",
          inputType: "text",
          placeholder: "e.g., 1,2,3",
          helperText: "Enter page numbers as shown on the PDF (comma-separated)",
          validation: { kind: LINK_FIELD_VALIDATION.REQUIRED_STRING },
        },
      ],
      linkConfirmModal: {
        title: "Confirm Verify and Proceed",
        body: "The entered pages will be verified and proceeded. Continue?",
        bodyWhenBypass:
          "The entered pages will be verified and proceeded with Bypass Auto Disposition enabled. Continue?",
      },
      linkSubmitApi: {
        method: "POST",
        url: "/ufl/dmr/link-fax",
        faxProgram: "DMR",
        errorMessagePrefix: "Failed to verify and proceed",
        pageLinkSourceFields: ["additionalField"],
        successNotification: {
          title: "Verify and Proceed",
          message: "Fax has been verified and proceeded successfully.",
        },
        navigateBackWhenCompletelyLinked: true,
      },
    },
  },
  [UNPROCESSED_FAX_PROGRAM_TYPE.UNKNOWN]: {
    [FAX_DETAILS_UI_RULES_DEFAULT_KEY]: {
      key: UNPROCESSED_FAX_PROGRAM_TYPE.UNKNOWN,
      headingText: "Link Fax Program Tag To Fax",
      showPriority: true,
      showTags: true,
      showLinkSection: false,
      showNotes: false,
      showMemberSwitchList: false,
      showDiscard: true,
      showAssociatedSwitch: false,
      navigateBackAfterTagSave: true,
      tagsButtonLabel: "Save and Move",
      showBypassAutoDisposition: false,
      bypassAutoDispositionLabel: "Bypass Auto Disposition",
      linkButtonLabel: "Link Switch(es)",
      linkFieldsConfig: [
        {
          name: "identifier",
          label: "Switch Id",
          apiField: "selectedGlobalSwitchId",
          inputType: "text",
          disabled: true,
          validation: { kind: LINK_FIELD_VALIDATION.STRING },
        },
        {
          name: "referenceField",
          label: "Change Order",
          apiField: "pageIndex",
          inputType: "number",
          validation: { kind: LINK_FIELD_VALIDATION.PAGE_NUMBER },
        },
        {
          name: "additionalField",
          label: "Additional Pages",
          apiField: "additionalPages",
          inputType: "text",
          placeholder: "e.g., 1,2,3",
          helperText: "Enter page numbers as shown on the PDF (comma-separated)",
          validation: { kind: LINK_FIELD_VALIDATION.OPTIONAL_STRING },
        },
      ],
      linkConfirmModal: {
        title: "Confirm Link Switches",
        body: "The entered pages will be linked to the selected switch(es) and removed from the pdf view. Continue?",
      },
    },
  },
};

export const getFaxDetailsUiRules = (faxType, faxProgramCode = "") => {
  const typeRules =
    FAX_DETAILS_UI_RULES[faxType] ||
    FAX_DETAILS_UI_RULES[UNPROCESSED_FAX_PROGRAM_TYPE.SWITCH];
  const programKey = faxProgramCode?.trim();
  if (programKey && typeRules[programKey]) {
    return typeRules[programKey];
  }
  return typeRules[FAX_DETAILS_UI_RULES_DEFAULT_KEY];
};

export const DEFAULT_UNPROCESSED_FAX_LIST_SORTING = [
  { id: "generatedDate", desc: true, isDefault: true },
];

export const DEFAULT_UNPROCESSED_FAX_LIST_FILTER = [
  { id: "associatedSwitchId", value: false, faxProgramType: [UNPROCESSED_FAX_PROGRAM_TYPE.SWITCH], isdefault: true, },
  {
    id: "status",
    value: "UNPROCESSED",
    faxProgramType: [
      UNPROCESSED_FAX_PROGRAM_TYPE.SWITCH,
      UNPROCESSED_FAX_PROGRAM_TYPE.NON_SWITCH,
    ],
    isdefault: true,
  },
  { id: "showMyRecentsOnly", value: false },
]

export const UFL_URL_FILTER_CONFIG = [
  {
    filterId: "tags",
    paramKey: "customerTags",
    valueType: QUERY_FILTER_TYPES.MULTI_SELECT,
    parseFromUrl: parseTagsFromUrl,
    serializeToUrl: serializeTagsToUrl,
    skipWhenSwitchAssociated: true,
  },
  {
    filterId: "faxPriority",
    paramKey: "faxPriority",
    valueType: QUERY_FILTER_TYPES.SINGLE_SELECT,
    valueMap: UNPROCESSED_FAX_PRIORITY,
    skipWhenSwitchAssociated: true,
  },
  {
    filterId: "status",
    paramKey: "status",
    valueType: QUERY_FILTER_TYPES.SINGLE_SELECT,
  },
  {
    filterId: "showMyRecentsOnly",
    paramKey: "showMyRecentsOnly",
    valueType: QUERY_FILTER_TYPES.BOOLEAN,
  },
  {
    filterId: "recentlyViewedUsers",
    paramKey: "recentlyViewedUsers",
    valueType: QUERY_FILTER_TYPES.MULTI_SELECT_IDS,
  },
  {
    filterId: "faxFileKey",
    paramKey: "faxFileKey",
    valueType: QUERY_FILTER_TYPES.TEXT,
  },
];

export const UNPROCESSED_FAX_LIST_COLUMNS = (
  faxTagList,
  switchAdminList,
  handleToggle,
  isShowMyRecentsDisabled,
  renderLastModifiedDateCol,
  faxDocumentHeader = "Fax Document",
  hiddenColumns = [],
  lockColumnSelection = [],
) => {
  const allColumns = [
    {
      header: faxDocumentHeader,
      accessor: 'faxFileKey',
      accessorKey: 'faxFileKey',
      enableSorting: true,
      columnType: ColumnTypes.Text,
      width: 400,
      enableColumnFilter: true,
      filterType: FilterTypes.SearchableInput,
    },
    {
      header: "Tags",
      accessor: "tags",
      accessorKey: "tags",
      size: 60,
      enableSorting: false,
      enableColumnFilter: true,
      columnType: ColumnTypes.Custom,
      customComponent: (value) => {
        if (value && value.length > 0) {
          return (
            <div>
              {value.map((tag, index) => (
                <Badge key={index} bg="secondary" className="me-1 mb-1">
                  {tag}
                </Badge>
              ))}
            </div>
          );
        }
        return <></>;
      },
      filterType: FilterTypes.MultiSelect,
      filterOptions: faxTagList,
    },
    {
      header: 'Fax Identifier',
      accessor: 'faxInfoShortIdentifier',
      accessorKey: 'faxInfoShortIdentifier',
      columnType: ColumnTypes.Text,
      filterType: FilterTypes.SearchableInput,
      enableColumnFilter: true,
      width: 300,
    },
    {
      header: 'Reference ID',
      accessor: 'faxReferenceId',
      accessorKey: 'faxReferenceId',
      columnType: ColumnTypes.Text,
      filterType: FilterTypes.SearchableInput,
      enableColumnFilter: true,
      width: 300,
    },
    {
      header: 'Priority',
      accessor: 'faxPriority',
      accessorKey: 'faxPriority',
      width: 200,
      columnType: ColumnTypes.Custom,
      enableColumnFilter: true,
      enableSorting: true,
      filterType: FilterTypes.SingleSelect,
      customComponent: (value) => (
        <WrapTextAndCenterAlign>
          {UNPROCESSED_FAX_PRIORITY[value]}
        </WrapTextAndCenterAlign>
      ),
      filterOptions:
        UNPROCESSED_FAX_PRIORITY_SINGLE_SELECT_OPTIONS,
    },
    {
      header: 'Associated Switch',
      accessor: 'associatedSwitchId',
      accessorKey: 'associatedSwitchId',
      width: 200,
      columnType: ColumnTypes.Text,
      enableColumnFilter: true,
      enableSorting: true,
      filterType: FilterTypes.SingleSelect,
      filterOptions: SWITCHES_MAP,
      filterReturnKey: true,
    },
    {
      header: 'Last Modified Date',
      accessor: 'lastModifiedDate',
      accessorKey: 'lastModifiedDate',
      columnType: ColumnTypes.DateColumn,
      enableSorting: true,
    },
    {
      header: 'Received Date',
      accessor: 'generatedDate',
      accessorKey: 'generatedDate',
      columnType: ColumnTypes.DateColumn,
      enableSorting: true,
    },
    {
      header: 'Prescriber NPI',
      accessor: 'prescriberNPI',
      accessorKey: 'prescriberNPI',
      width: 200,
      columnType: ColumnTypes.Text,
      enableColumnFilter: true,
      enableSorting: true,
      filterReturnKey: true,
      filterType: FilterTypes.SearchableInput,
    },
    {
      header: 'Status',
      accessor: 'status',
      accessorKey: 'status',
      width: 200,
      columnType: ColumnTypes.Custom,
      customComponent: (value) => (
        <WrapTextAndCenterAlign>
          {UNPROCESSED_FAX_STATUSES[value]}
        </WrapTextAndCenterAlign>
      ),
      enableSorting: true,
      enableColumnFilter: true,
      filterType: FilterTypes.SingleSelect,
      filterOptions: [
        { key: "", value: "" },
        ...Object.keys(UNPROCESSED_FAX_STATUSES).map(status => ({ key: status, value: status }))
      ],
    },
    {
      header: "Recently Viewed Users",
      accessor: 'recentlyViewedUsers',
      accessorKey: 'recentlyViewedUsers',
      columnType: ColumnTypes.Custom,
      enableColumnFilter: true,
      enableSorting: true,
      filterType: FilterTypes.MultiSelect,
      filterOptions: switchAdminList,
      hasToggleButton: true,
      handleToggle,
      isDisabled: isShowMyRecentsDisabled(),
      customComponent: (value, row) =>
        renderLastModifiedDateCol(
          row.original.lastViewedAt,
          row.original.lastViewedBy
        ),
    },
  ];

  const filteredColumns = allColumns.filter(
    (col) => !hiddenColumns.includes(col.accessorKey)
  );

  return filteredColumns.map(col => {
    const lockedColumn = lockColumnSelection.find(lock => lock.id === col.accessorKey);
    if (lockedColumn) {
      col.isSelectionLocked = true;
      if (lockedColumn.value) col.lockedValues = lockedColumn.value;
    }
    return col;
  });
};

export const UNPROCESSED_FAX_LIST_HIDDEN_COLUMNS = {
  [UNPROCESSED_FAX_PROGRAM_TYPE.SWITCH] : ["prescriberNPI", "faxReferenceId"],
  [UNPROCESSED_FAX_PROGRAM_TYPE.NON_SWITCH]: ["associatedSwitchId", "faxInfoShortIdentifier"],
  [UNPROCESSED_FAX_PROGRAM_TYPE.UNKNOWN]: ["tags", "faxInfoShortIdentifier", "prescriberNPI", "associatedSwitchId", "faxReferenceId", "status"]
}
